import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, Calendar, Image, Video, CheckCircle, AlertCircle, FileType, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

function DesignerPortal() {
  const [token, setToken] = useState('');
  const [isValidated, setIsValidated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fechaUso, setFechaUso] = useState('');
  const [fechaExpiracion, setFechaExpiracion] = useState('');
  const [categoria, setCategoria] = useState('difusion');
  const [previews, setPreviews] = useState([]);
  const [myFlyers, setMyFlyers] = useState([]);

  useEffect(() => {
    // Obtener token de la URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    
    if (urlToken) {
      setToken(urlToken);
      validateToken(urlToken);
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async (tokenToValidate) => {
    try {
      await axios.get(`${API}/api/flyers/designer-token/validate`, {
        params: { token: tokenToValidate }
      });
      setIsValidated(true);
      loadMyFlyers(tokenToValidate);
    } catch (error) {
      console.error('Error validando token:', error);
      toast.error('Token inválido o expirado');
      setIsValidated(false);
    } finally {
      setLoading(false);
    }
  };

  const loadMyFlyers = async (tokenToUse = token) => {
    try {
      const response = await axios.get(`${API}/api/flyers/`, {
        params: { include_expired: false }
      });
      // Filtrar solo los flyers subidos por el diseñador
      const designerFlyers = response.data.flyers.filter(f => f.uploaded_by === 'designer');
      setMyFlyers(designerFlyers);
    } catch (error) {
      console.error('Error cargando flyers:', error);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setSelectedFiles(files);

    // Generar previews para todos los archivos
    const newPreviews = [];
    files.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews[index] = { type: 'image', url: reader.result, name: file.name };
          setPreviews([...newPreviews]);
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        newPreviews[index] = { type: 'video', url: URL.createObjectURL(file), name: file.name };
        setPreviews([...newPreviews]);
      }
    });
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !fechaUso || !fechaExpiracion) {
      toast.error('Selecciona al menos un archivo, fecha de uso y fecha de expiración');
      return;
    }

    try {
      setUploading(true);
      let successCount = 0;
      let errorCount = 0;

      // Subir cada archivo
      for (const file of selectedFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('fecha_uso', fechaUso);
          formData.append('fecha_expiracion', fechaExpiracion);
          formData.append('categoria', categoria);
          formData.append('uploaded_by', 'designer');

          await axios.post(`${API}/api/flyers/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          successCount++;
        } catch (err) {
          console.error(`Error subiendo ${file.name}:`, err);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`¡${successCount} flyer(s) subido(s) exitosamente!`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} flyer(s) fallaron al subir`);
      }

      setSelectedFiles([]);
      setFechaUso('');
      setFechaExpiracion('');
      setCategoria('difusion');
      setPreviews([]);
      loadMyFlyers();
    } catch (error) {
      console.error('Error subiendo flyers:', error);
      toast.error('Error al subir flyers');
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };


  const handleDelete = async (flyerId, filename) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${filename}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/flyers/${flyerId}`);
      toast.success('Flyer eliminado exitosamente');
      loadMyFlyers();
    } catch (error) {
      console.error('Error eliminando flyer:', error);
      toast.error('Error al eliminar el flyer');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validando acceso...</p>
        </div>
      </div>
    );
  }

  if (!isValidated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-6 h-6" />
              Acceso Denegado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              No tienes permiso para acceder a esta página. 
              Necesitas un enlace válido del administrador.
            </p>
            <p className="text-sm text-gray-500">
              Si crees que esto es un error, contacta al administrador del sistema.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Portal del Diseñador</h1>
          <p className="text-gray-600">Sube flyers para difusión</p>
        </div>

        {/* Upload Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Subir Nuevo Flyer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Seleccionar Archivos (Múltiples)
                </label>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  multiple
                  className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none p-2"
                />
                <p className="mt-2 text-sm text-gray-500">
                  ✨ Puedes seleccionar múltiples archivos a la vez<br/>
                  📷 Imágenes: JPG, PNG, GIF, WebP (máx 10MB)<br />
                  🎥 Videos: MP4, MOV, AVI, MKV (máx 50MB)
                </p>
              </div>

              {selectedFiles.length > 0 && (
                <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50">
                  <p className="text-sm font-medium mb-2 text-blue-900">
                    {selectedFiles.length} archivo(s) seleccionado(s):
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm bg-white px-3 py-2 rounded shadow-sm">
                        {file.type.startsWith('image/') ? (
                          <Image className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        ) : (
                          <Video className="w-4 h-4 text-purple-600 flex-shrink-0" />
                        )}
                        <span className="truncate flex-1">{file.name}</span>
                        <span className="text-gray-500 text-xs flex-shrink-0">
                          {(file.size / (1024 * 1024)).toFixed(1)} MB
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block mb-2 font-medium text-blue-700">
                    📅 ¿Para qué día es este flyer? *
                  </label>
                  <input
                    type="date"
                    value={fechaUso}
                    onChange={(e) => setFechaUso(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="block w-full px-4 py-2 border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                  <p className="mt-2 text-sm text-blue-600 font-medium">
                    Ejemplo: Si es para el domingo, selecciona el domingo
                  </p>
                </div>

                <div>
                  <label className="block mb-2 font-medium text-purple-700">
                    🎯 ¿Para qué es este flyer? *
                  </label>
                  <select
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value)}
                    className="block w-full px-4 py-2 border-2 border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="difusion">📢 Difusión (Redes Sociales / WhatsApp)</option>
                    <option value="app_pppoker">🃏 App PPPoker (Dimensiones específicas)</option>
                  </select>
                  <p className="mt-2 text-sm text-purple-600 font-medium">
                    {categoria === 'app_pppoker' 
                      ? '⚠️ Asegúrate de usar las dimensiones correctas para la app'
                      : 'Para compartir en redes sociales y grupos'}
                  </p>
                </div>

                <div>
                  <label className="block mb-2 font-medium text-gray-700">
                    Fecha de Expiración
                  </label>
                  <input
                    type="date"
                    value={fechaExpiracion}
                    onChange={(e) => setFechaExpiracion(e.target.value)}
                    min={fechaUso || new Date().toISOString().split('T')[0]}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    ⏰ El flyer se eliminará automáticamente después de esta fecha
                  </p>
                </div>
              </div>

              <Button
                onClick={handleUpload}
                disabled={selectedFiles.length === 0 || !fechaUso || !fechaExpiracion || uploading}
                className="w-full py-3 text-lg"
                size="lg"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Subiendo {selectedFiles.length} archivo(s)...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Subir {selectedFiles.length > 0 ? `${selectedFiles.length} Flyer(s)` : 'Flyer'}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* My Flyers Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Mis Flyers Subidos ({myFlyers.length})</span>
              <Button variant="outline" size="sm" onClick={() => loadMyFlyers()}>
                Actualizar
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myFlyers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Upload className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No has subido flyers aún</p>
                <p className="text-sm mt-2">Los flyers que subas aparecerán aquí</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {myFlyers.map((flyer) => (
                  <div
                    key={flyer.id}
                    className="border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
                  >
                    <div className="h-40 bg-gray-100 flex items-center justify-center overflow-hidden relative">
                      {flyer.file_type === 'image' ? (
                        <img 
                          src={`${API}/api/flyers/${flyer.id}/download`}
                          alt={flyer.original_filename}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<svg class="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>';
                          }}
                        />
                      ) : (
                        <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                          <Video className="w-12 h-12 text-white opacity-50" />
                          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                            Video
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {flyer.file_type === 'image' ? (
                            <Image className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          ) : (
                            <Video className="w-4 h-4 text-purple-500 flex-shrink-0" />
                          )}
                          <span className="font-medium text-sm truncate">
                            {flyer.original_filename}
                          </span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold flex-shrink-0 ${
                          flyer.categoria === 'app_pppoker' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {flyer.categoria === 'app_pppoker' ? '🃏' : '📢'}
                        </span>
                      </div>
                      
                      <div className="text-xs space-y-1.5">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <FileType className="w-3.5 h-3.5 text-gray-400" />
                          <span>{flyer.file_format.toUpperCase()}</span>
                          <span className="text-gray-400">•</span>
                          <span>{formatFileSize(flyer.file_size)}</span>
                        </div>
                        {flyer.fecha_uso && (
                          <div className="flex items-center gap-1.5 text-blue-600 font-semibold bg-blue-50 px-2 py-1 rounded -mx-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>Para: {formatDate(flyer.fecha_uso)}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span>Expira: {formatDate(flyer.fecha_expiracion)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-green-600">
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span className="font-medium">Subido correctamente</span>
                        </div>
                      </div>

                      {/* Botón de eliminar */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(flyer.id, flyer.original_filename)}
                          className="w-full text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Eliminar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Footer */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-2">Información importante:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li>Los flyers se eliminan automáticamente después de la fecha de expiración</li>
                <li>Asegúrate de subir los flyers con anticipación a la fecha de uso</li>
                <li>Para flyers de domingos, puedes subir tanto imágenes como videos</li>
                <li>Si subes un archivo incorrecto, contacta al administrador para eliminarlo</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DesignerPortal;
